import { apiClient } from "./api-client";

export interface LeaderboardUser {
  id: string;
  username: string;
  email: string;
  totalContributions: number;
  totalEarnings: number;
  rank: number;
  avatarUrl?: string;
  languages: string[];
  displayName: string;
  contributions: number;
  earnings: number;

}

export interface LeaderboardResponse {
  users: LeaderboardUser[];
  totalUsers: number;
  userRank?: number;
  userScore?: number;
}

export const leaderboardApi = {
  /**
   * Get leaderboard for a specific language
   * @param language Language code (e.g., 'fr', 'ewe', etc.)
   * @param metric Metric to rank by: 'contributions' or 'earnings'
   * @param limit Number of top users to return (default: 100)
   */
  async getLeaderboard(language: string, metric: 'contributions' | 'earnings' = 'contributions', limit: number = 100) {
    try {
      const { data } = await apiClient.get<LeaderboardResponse>("/users/stats/leaderboard", {
        params: {
          language,
          metric,
          limit,
        },
      });
      return data;
    } catch (error) {
      console.error('Failed to fetch leaderboard', error);
      throw error;
    }
  },

  /**
   * Get leaderboard for a specific language
   * @param language Language code (e.g., 'fr', 'ewe', etc.)
   * @param metric Metric to rank by: 'contributions' or 'earnings'
   * @param limit Number of top users to return (default: 100)
   */
  async getLeaderboard(language: string, metric: 'contributions' | 'earnings' = 'contributions', limit: number = 100) {
    try {
      const { data } = await apiClient.get<LeaderboardResponse>("/users/stats/leaderboard", {
        params: {
          language,
          metric,
          limit,
        },
      });
      return data;
    } catch (error) {
      console.error('Failed to fetch leaderboard', error);
      throw error;
    }
  },

  /**
   * Get user's rank in leaderboard
   * @param userId User ID
   * @param language Language code
   * @param metric Metric to rank by
   */
  async getUserRank(userId: string, language: string, metric: 'contributions' | 'earnings' = 'contributions') {
    try {
      const { data } = await apiClient.get<LeaderboardResponse>(`/users/stats/leaderboard/user/${userId}`, {
        params: {
          language,
          metric,
        },
      });
      return data;
    } catch (error) {
      console.error('Failed to fetch user rank', error);
      throw error;
    }
  },

  /**
   * Get leaderboard for a specific language (normalized)
   * This calls the backend endpoint that returns raw rows and ensures we
   * sort by contributions descending and assign `rank` = 1..N.
   */
  async getLanguageLeaderboard(language: string, limit: number = 100, period: 'week' | 'month' | 'all' = 'all') {
    try {
      const { data } = await apiClient.get<unknown>(`/users/stats/leaderboard/${language}`, {
        params: { limit, period },
      });

      const rows: Array<Record<string, unknown>> = Array.isArray(data) ? data as Array<Record<string, unknown>> : ((data as Record<string, unknown>)?.users as Array<Record<string, unknown>> || []);

      const sorted = (rows || [])
        .slice()
        .sort((a, b) => (Number(b.contributions) || 0) - (Number(a.contributions) || 0))
        .slice(0, limit);

      return sorted.map((r, i) => ({
        rank: i + 1,
        contributions: Number(r.contributions) || 0,
        displayName: r.displayName || (r.denonymizedId ? `Utilisateur ${r.denonymizedId}` : 'Anonyme'),
        // Dropped before: the per-language board could not find the signed-in
        // contributor by username, nor show the handle.
        username: r.username,
        userId: r.userId,
        languages: r.languages,
      }));
    } catch (error) {
      console.error('Failed to fetch language leaderboard', error);
      throw error;
    }
  },

  /**
   * Get global leaderboard (all languages combined)
   * @param metric Metric to rank by
   * @param limit Number of top users
   */
  async getGlobalLeaderboard(metric: 'contributions' | 'earnings' = 'contributions', limit: number = 100, period: 'week' | 'month' | 'all' = 'all') {
    try {
      const { data } = await apiClient.get<LeaderboardResponse>("/users/stats/leaderboard", {
        params: {
          metric,
          limit,
          period,
        },
      });
      return data;
    } catch (error) {
      console.error('Failed to fetch global leaderboard', error);
      throw error;
    }
  },
};
